import Modal from "@mui/material/Modal";
import React, { useState, useEffect } from "react";
import axiosClient from "../axiosClient";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import EventAvailableOutlinedIcon from "@mui/icons-material/EventAvailableOutlined";
import {
  Typography,
  TextField,
  Button,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  ListItem,
  ListItemText,
  IconButton,
} from "@mui/material";
import { useStateContext } from "../context/ContextProvider";
import { useTranslation } from "react-i18next";
import CalendarCheckbox from "./SmallCalendar";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: { xs: "90vw", sm: "80vw", md: 800 },
  maxHeight: "90vh",
  overflowY: "auto",
  bgcolor: "background.paper",
  border: "2px solid #000",
  boxShadow: 24,
  p: { xs: 2, sm: 4 },
  borderRadius: 2,
};

export default function AppointmentEditModal({
  id,
  open,
  handleClose,
  getAppointment,
  isDelete = false,
}) {
  const [staffUsers, setStaffUsers] = useState([]);
  const [clientUsers, setClientUsers] = useState([]);
  const [appointment, setAppointment] = useState({
    appointment_date: "",
    appointment_start: "",
    appointment_end: "",
    client_id: "",
    staff_id: "",
    comments: [],
    earlier_date: "",
  });
  const [selectedClient, setSelectedClient] = useState("");
  const [selectedStaff, setSelectedStaff] = useState("");
  const [selectedDateCalendar, setSelectedDateCalendar] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showCalendar, setShowCalendar] = useState(false);

  const { t } = useTranslation();
  const { user } = useStateContext();

  const handleChange = (e) => {
    setAppointment({
      ...appointment,
      [e.target.name]: e.target.value,
    });
  };

  useEffect(() => {
    if (user?.user_role === "ADMIN" || user?.user_role === "STAFF") {
      Promise.all([
        axiosClient.get("api/staff-users/"),
        axiosClient.get("api/client-users/"),
      ])
        .then(([staffRes, clientRes]) => {
          setStaffUsers(staffRes?.data);
          setClientUsers(clientRes?.data);
        })
        .catch((error) => {
          console.log(error);
        });
    }
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (id && open) {
      const fetchData = async () => {
        try {
          setIsLoading(true);
          const getAppointmentResponse = await axiosClient.get(
            `api/appointment/${id}/`
          );
          console.log(getAppointmentResponse?.data);
          const getAppointmentComments = await axiosClient.get(
            `api/comments/${id}/`
          );

          setAppointment({
            ...getAppointmentResponse?.data,
            appointment_date: getAppointmentResponse?.data?.appointment_date || "",
            earlier_date: getAppointmentResponse?.data?.earlier_date || "",
            comments: getAppointmentComments.data,
          });
          setSelectedClient(getAppointmentResponse?.data?.client_id);
          setSelectedStaff(getAppointmentResponse?.data?.staff_id);
          setSelectedDateCalendar(
            getAppointmentResponse?.data?.earlier_date
              ? new Date(getAppointmentResponse?.data?.earlier_date)
              : null
          );
        } catch (error) {
          console.error("Error fetching appointment:", error);
        } finally {
          setIsLoading(false);
        }
      };
      fetchData();
    }
  }, [open, id]);

  // useEffect(() => {
  //     if (selectedStaff) {
  //         const filteredData = clientUsers.filter(user => user.assigned_staff === selectedStaff);
  //         setClientUsers(filteredData);
  //     }
  // }, [selectedStaff])

  // Synchronize selectedDateCalendar with appointment.earlier_date
  useEffect(() => {
    if (selectedDateCalendar) {
      setAppointment((prev) => ({
        ...prev,
        earlier_date: formatDate(selectedDateCalendar),
      }));
    } else {
      setAppointment((prev) => ({
        ...prev,
        earlier_date: "",
      }));
    }
  }, [selectedDateCalendar]);

  const handleDelete = () => {
    axiosClient
      .delete(`api/appointment/${id}/`)
      .then((res) => {
        getAppointment();
        handleClose();
      })
      .catch((error) => {
        console.error("Error deleting appointment:", error);
      });
  };

  const formatDate = (date) => {
    if (!date) return "";
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    // Validate required fields
    if (!selectedClient && !appointment?.client_id) {
      console.error("Error: Client ID is required");
      alert(t("Client ID is required"));
      return;
    }
    if (!selectedStaff && !appointment?.staff_id) {
      console.error("Error: Staff ID is required");
      alert(t("Staff ID is required"));
      return;
    }
    if (!appointment?.appointment_date) {
      console.error("Error: Appointment date is required");
      alert(t("Appointment date is required"));
      return;
    }
    if (!appointment?.appointment_start) {
      console.error("Error: Appointment start time is required");
      alert(t("Appointment start time is required"));
      return;
    }
    if (!appointment?.appointment_end) {
      console.error("Error: Appointment end time is required");
      alert(t("Appointment end time is required"));
      return;
    }

    const updatedAppointment = {
      ...appointment,
      client_id: selectedClient ? selectedClient : appointment?.client_id,
      staff_id: selectedStaff ? selectedStaff : appointment?.staff_id,
      appointment_start_time_str:
        appointment?.appointment_start?.length === 5
          ? `${appointment?.appointment_start}:00`
          : appointment?.appointment_start,
      appointment_finish_time_str:
        appointment?.appointment_end?.length === 5
          ? `${appointment?.appointment_end}:00`
          : appointment?.appointment_end,
      earlier_date: selectedDateCalendar ? formatDate(selectedDateCalendar) : "",
    };

    console.log("Updated Appointment:", updatedAppointment);

    axiosClient
      .put(`api/appointment-edit/${id}/`, updatedAppointment)
      .then((res) => {
        getAppointment();
        handleClose();
      })
      .catch((error) => {
        console.log(error);
      });
  };

  const handleModalClose = (event, reason) => {
    // Prevent closing on backdrop click or Escape key press
    if (reason === "backdropClick" || reason === "escapeKeyDown") {
      return;
    }
    setAppointment({
      appointment_date: "",
      appointment_start: "",
      appointment_end: "",
      client_id: "",
      staff_id: "",
      comments: [],
      earlier_date: "",
    });
    setSelectedClient("");
    setSelectedStaff("");
    setSelectedDateCalendar(null);
    setIsLoading(true);
    setShowCalendar(false);
    handleClose();
  };

  const handleCalendarOpen = () => {
    setShowCalendar(true);
  };

  return (
    <Modal
      open={open}
      onClose={handleModalClose}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
      disableEscapeKeyDown
    >
      <Box sx={style}>
        {isLoading ? (
          <Typography>Loading...</Typography>
        ) : (
          <>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 1,
              }}
            >
              <Typography
                id="modal-modal-title"
                sx={{ fontSize: { xs: "16px", sm: "18px" }, fontWeight: "500" }}
                variant="h4"
                component="h2"
              >
                {t("Edit Appointment")}
              </Typography>
              <Typography
                onClick={handleModalClose}
                sx={{ cursor: "pointer" }}
                id="modal-modal-close"
                variant="h6"
                component="h2"
              >
                <CloseOutlinedIcon />
              </Typography>
            </Box>
            <hr
              style={{
                border: "none",
                borderBottom: "1px solid #e6ebf1",
                marginBottom: "25px",
              }}
            />
            <form onSubmit={handleSubmit}>
              <Box sx={{ "& .MuiTextField-root": { marginBottom: "1rem" } }}>
                <FormControl
                  fullWidth
                  variant="outlined"
                  sx={{ marginBottom: "1rem" }}
                >
                  <InputLabel id="staff-select-label">{t("Select Staff")}</InputLabel>
                  <Select
                    labelId="staff-select-label"
                    value={selectedStaff}
                    onChange={(e) => setSelectedStaff(e.target.value)}
                    label={t("Select Staff")}
                    disabled
                  >
                    {staffUsers.map((st, i) => (
                      <MenuItem
                        key={i}
                        value={st.id}
                        lastname={st.custom_user.last_name}
                      >
                        {`${st.custom_user.first_name} ${st.custom_user.last_name}`}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl
                  fullWidth
                  variant="outlined"
                  sx={{ marginBottom: "1rem" }}
                >
                  <InputLabel id="client-select-label">{t("Select Client")}</InputLabel>
                  <Select
                    labelId="client-select-label"
                    value={selectedClient}
                    onChange={(e) => setSelectedClient(e.target.value)}
                    label={t("Select Client")}
                    disabled
                  >
                    {clientUsers.map((user, i) => (
                      <MenuItem
                        key={i}
                        value={user.id}
                        lastname={user.custom_user.last_name}
                      >
                        {`${user.custom_user.first_name} ${user.custom_user.last_name}`}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <TextField
                  fullWidth
                  type="date"
                  label={t("Date of Appointment")}
                  variant="outlined"
                  name="appointment_date"
                  InputLabelProps={{ shrink: true }}
                  value={appointment?.appointment_date || ""}
                  onChange={handleChange}
                  sx={{ mb: "1rem" }}
                />
                <TextField
                  fullWidth
                  type="time"
                  label={t("Time of Appointment (start)")}
                  variant="outlined"
                  name="appointment_start"
                  InputLabelProps={{ shrink: true }}
                  value={appointment?.appointment_start || ""}
                  onChange={handleChange}
                  sx={{ mb: "1rem" }}
                />
                <TextField
                  fullWidth
                  type="time"
                  label={t("Time of Appointment (end)")}
                  variant="outlined"
                  name="appointment_end"
                  InputLabelProps={{ shrink: true }}
                  value={appointment?.appointment_end || ""}
                  onChange={handleChange}
                  sx={{ mb: "1rem" }}
                />
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    mb: "1rem",
                    p: 2,
                    border: "1px solid",
                    borderColor: "grey.300",
                    borderRadius: 2,
                    bgcolor: "background.default",
                    boxShadow: 1,
                    transition: "box-shadow 0.3s ease-in-out",
                    "&:hover": {
                      boxShadow: 3,
                    },
                  }}
                >
                  <Box sx={{ flexGrow: 1 }}>
                    <Typography
                      variant="subtitle1"
                      sx={{
                        fontWeight: "medium",
                        color: "text.primary",
                        mb: 0.5,
                      }}
                    >
                      {t("Earlier Appointment Date")}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: appointment?.earlier_date ? "text.primary" : "text.secondary",
                        fontStyle: appointment?.earlier_date ? "normal" : "italic",
                      }}
                    >
                      {appointment?.earlier_date
                        ? appointment.earlier_date
                        : t("No earlier appointment set")}
                    </Typography>
                  </Box>
                  <IconButton
                    onClick={handleCalendarOpen}
                    color="primary"
                    sx={{
                      bgcolor: "background.paper",
                      borderRadius: "50%",
                      p: 1,
                      "&:hover": {
                        bgcolor: "primary.main",
                        color: "grey.200",
                        transform: "scale(1.1)",
                        transition: "all 0.2s ease-in-out",
                      },
                    }}
                    aria-label={t("Select Earlier Appointment Date")}
                  >
                    <EventAvailableOutlinedIcon fontSize="medium" />
                  </IconButton>
                </Box>
                {showCalendar && (
                  <CalendarCheckbox
                    selectedDateCalendar={selectedDateCalendar || new Date()}
                    setSelectedDateCalendar={setSelectedDateCalendar}
                    selectedClient={selectedClient}
                    openCalendar={showCalendar}
                    setOpenCalendar={setShowCalendar}
                  />
                )}
                <Box sx={{ my: 2 }}>
                  <Typography
                    id="modal-modal-comments"
                    sx={{ fontSize: { xs: "16px", sm: "18px" }, fontWeight: "500" }}
                    variant="h4"
                    component="h2"
                  >
                    {t("Comments")}
                  </Typography>
                  <hr
                    style={{
                      border: "none",
                      borderBottom: "1px solid #e6ebf1",
                      marginBottom: 1,
                    }}
                  />
                  {appointment?.comments?.length > 0 ? (
                    appointment.comments.map((item, i) => (
                      <ListItem key={i}>
                        <ListItemText
                          secondary={
                            <React.Fragment>
                              <Typography
                                component="span"
                                variant="body2"
                                sx={{
                                  color: "text.primary",
                                  display: "inline",
                                  mr: 1,
                                  fontWeight: "bold",
                                }}
                              >
                                {item?.comment_text}
                              </Typography>
                              {`— by ${item?.user?.username}`}
                            </React.Fragment>
                          }
                        />
                      </ListItem>
                    ))
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      {t("No comments available")}
                    </Typography>
                  )}
                </Box>
              </Box>
              <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  sx={{ minWidth: { xs: "100px", sm: "120px" } }}
                >
                  {t("Submit")}
                </Button>
                {isDelete && (
                  <Button
                    type="button"
                    onClick={handleDelete}
                    variant="contained"
                    color="error"
                    sx={{ ml: 1, minWidth: { xs: "100px", sm: "120px" } }}
                  >
                    {t("Delete")}
                  </Button>
                )}
              </Box>
            </form>
          </>
        )}
      </Box>
    </Modal>
  );
}